import React, { useEffect } from 'react';
import {
  initialContentScopeFilter,
  hasActiveContentScopeFilter,
  BRAND_FILTER_UNASSIGNED,
  brandFilterSelectValue
} from '../utils/contentScope';

const SUPER_SCOPE_OPTIONS = [
  { value: 'unassigned', label: 'Unassigned (not visible anywhere)' },
  { value: 'all', label: 'All brands & branches' },
  { value: 'brand', label: 'Specific brand(s)' },
  { value: 'branch', label: 'Specific branch(es)' }
];

const BRAND_ADMIN_SCOPE_OPTIONS = [
  { value: 'unassigned', label: 'Unassigned (not visible to branches)' },
  { value: 'brand', label: 'All branches in this brand' },
  { value: 'branch', label: 'Specific branch(es)' }
];

const BRANCH_ADMIN_SCOPE_OPTIONS = [
  { value: 'branch', label: 'This branch only' }
];

const toggleId = (list, id) => {
  const sid = String(id);
  const next = new Set((list || []).map(String));
  if (next.has(sid)) next.delete(sid);
  else next.add(sid);
  return [...next];
};

const ContentScopeFields = ({
  variant = 'form',
  value,
  onChange,
  brands = [],
  salons = [],
  disabled = false,
  lockedBrandId = null,
  lockedSalonId = null,
  allowedScopes = null
}) => {
  const {
    scope = 'unassigned',
    brandId = '',
    brandIds = [],
    salonId = '',
    salonIds = []
  } = value || {};

  const effectiveBrandIds = brandIds?.length ? brandIds.map(String) : brandId ? [String(brandId)] : [];
  const effectiveSalonIds = salonIds?.length ? salonIds.map(String) : salonId ? [String(salonId)] : [];

  const isBrandAdminForm =
    Array.isArray(allowedScopes) &&
    allowedScopes.includes('brand') &&
    allowedScopes.includes('unassigned') &&
    !allowedScopes.includes('all');

  const isBranchAdminForm =
    Array.isArray(allowedScopes) &&
    allowedScopes.length === 1 &&
    allowedScopes[0] === 'branch';

  const scopeOptions = (() => {
    if (isBranchAdminForm) return BRANCH_ADMIN_SCOPE_OPTIONS;
    if (isBrandAdminForm) return BRAND_ADMIN_SCOPE_OPTIONS;
    if (allowedScopes && allowedScopes.length) {
      return SUPER_SCOPE_OPTIONS.filter((o) => allowedScopes.includes(o.value));
    }
    return SUPER_SCOPE_OPTIONS;
  })();

  const setField = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    if (!onChange) return;
    const patch = {};
    if (lockedBrandId) {
      if (String(brandId) !== String(lockedBrandId)) patch.brandId = lockedBrandId;
      if (!effectiveBrandIds.includes(String(lockedBrandId))) {
        patch.brandIds = [lockedBrandId];
      }
    }
    if (lockedSalonId) {
      patch.salonId = lockedSalonId;
      patch.salonIds = [lockedSalonId];
      patch.scope = variant === 'filter' ? (scope || 'branch') : 'branch';
      if (lockedBrandId) {
        patch.brandId = lockedBrandId;
        patch.brandIds = [lockedBrandId];
      }
    }
    if (Object.keys(patch).length) onChange({ ...value, ...patch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedBrandId, lockedSalonId]);

  const brandLocked = Boolean(lockedBrandId);
  const salonLocked = Boolean(lockedSalonId);

  const visibleBrands = lockedBrandId
    ? brands.filter((b) => String(b._id) === String(lockedBrandId))
    : brands;

  const filteredSalons = salons.filter((s) => {
    if (lockedSalonId) return String(s._id) === String(lockedSalonId);
    if (effectiveBrandIds.length) {
      return effectiveBrandIds.includes(String(s.brand?._id || s.brand));
    }
    if (lockedBrandId) return String(s.brand?._id || s.brand) === String(lockedBrandId);
    return true;
  });

  if (variant === 'filter') {
    const filterScope = scope || '';
    const brandValue = brandFilterSelectValue(value, lockedBrandId);
    const showUnassigned = !isBranchAdminForm && (!allowedScopes || allowedScopes.includes('unassigned'));
    const showBranch = filterScope !== 'unassigned' && !salonLocked;

    const handleClear = () => {
      const cleared = initialContentScopeFilter();
      if (lockedBrandId) cleared.brandId = lockedBrandId;
      if (lockedSalonId) {
        cleared.scope = 'branch';
        cleared.salonId = lockedSalonId;
        if (lockedBrandId) cleared.brandId = lockedBrandId;
      }
      onChange(cleared);
    };

    return (
      <div className="admin-card filter-card">
        <h3>Filters</h3>
        <div className="filter-grid">
          <div className="form-group">
            <label>Brand</label>
            <select
              value={brandValue}
              disabled={disabled || salonLocked}
              onChange={(e) => {
                const next = e.target.value;
                if (next === BRAND_FILTER_UNASSIGNED) {
                  setField({
                    scope: 'unassigned',
                    brandId: lockedBrandId || '',
                    salonId: ''
                  });
                  return;
                }
                setField({
                  scope: '',
                  brandId: next || lockedBrandId || '',
                  salonId: lockedSalonId || ''
                });
              }}
            >
              <option value="">All</option>
              {showUnassigned && (
                <option value={BRAND_FILTER_UNASSIGNED}>Unassigned</option>
              )}
              {!brandLocked && visibleBrands.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{b.brandCode ? ` (${b.brandCode})` : ''}
                </option>
              ))}
            </select>
          </div>

          {showBranch && (
            <div className="form-group">
              <label>Branch</label>
              <select
                value={lockedSalonId || salonId}
                disabled={disabled || salonLocked || (Boolean(!brandLocked) && !brandId)}
                onChange={(e) => setField({ salonId: e.target.value })}
              >
                {!salonLocked && <option value="">All branches</option>}
                {filteredSalons.map((s) => (
                  <option key={s._id} value={s._id}>
                    {[s.name, s.branch].filter(Boolean).join(' ')}
                    {s.branchCode ? ` (${s.branchCode})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group filter-actions">
            {brandLocked && salonLocked ? (
              <p className="form-hint" style={{ margin: 0 }}>
                Scope locked to your branch.
              </p>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={disabled || !hasActiveContentScopeFilter(value)}
                onClick={handleClear}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-scope-fields">
      <div className="form-group">
        <label>Visibility *</label>
        <select
          value={scope}
          disabled={disabled || salonLocked}
          onChange={(e) => {
            const nextScope = e.target.value;
            setField({
              scope: nextScope,
              brandId: nextScope === 'all' ? '' : (lockedBrandId || brandId),
              brandIds: nextScope === 'all' ? [] : (lockedBrandId ? [lockedBrandId] : effectiveBrandIds),
              salonId: nextScope === 'branch' ? (lockedSalonId || salonId) : '',
              salonIds: nextScope === 'branch' ? (lockedSalonId ? [lockedSalonId] : effectiveSalonIds) : []
            });
          }}
          required
        >
          {scopeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="form-hint">
          {scope === 'unassigned'
            ? 'Not visible on any brand or branch until you assign it.'
            : isBrandAdminForm
              ? 'Publish to every branch under your brand, or only selected branches.'
              : 'Assign where this content should appear. Unassigned stays hidden.'}
        </p>
      </div>

      {scope === 'brand' && !brandLocked && (
        <div className="form-group">
          <label>Brand(s) *</label>
          <div className="checkbox-list" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
            {visibleBrands.map((b) => {
              const checked = effectiveBrandIds.includes(String(b._id));
              return (
                <label key={b._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => {
                      const next = toggleId(effectiveBrandIds, b._id);
                      setField({ brandIds: next, brandId: next[0] || '' });
                    }}
                  />
                  <span>{b.name}{b.brandCode ? ` (${b.brandCode})` : ''}</span>
                </label>
              );
            })}
          </div>
          {!effectiveBrandIds.length && (
            <p className="form-hint" style={{ color: '#ff6b6b' }}>Select at least one brand.</p>
          )}
        </div>
      )}

      {scope === 'brand' && brandLocked && (
        <p className="form-hint">Will be available to all branches under your brand.</p>
      )}

      {scope === 'branch' && (
        <>
          {!salonLocked && !brandLocked && (
            <div className="form-group">
              <label>Filter branches by brand</label>
              <select
                value={effectiveBrandIds[0] || ''}
                disabled={disabled}
                onChange={(e) => setField({
                  brandId: e.target.value,
                  brandIds: e.target.value ? [e.target.value] : [],
                  salonIds: [],
                  salonId: ''
                })}
              >
                <option value="">All brands</option>
                {visibleBrands.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}{b.brandCode ? ` (${b.brandCode})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label>Branch(es) *</label>
            <div className="checkbox-list" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
              {filteredSalons.map((s) => {
                const checked = effectiveSalonIds.includes(String(s._id));
                return (
                  <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={checked || salonLocked}
                      disabled={disabled || salonLocked}
                      onChange={() => {
                        if (salonLocked) return;
                        const next = toggleId(effectiveSalonIds, s._id);
                        setField({ salonIds: next, salonId: next[0] || '' });
                      }}
                    />
                    <span>
                      {[s.name, s.branch].filter(Boolean).join(' ')}
                      {s.branchCode ? ` (${s.branchCode})` : ''}
                    </span>
                  </label>
                );
              })}
            </div>
            {!effectiveSalonIds.length && !salonLocked && (
              <p className="form-hint" style={{ color: '#ff6b6b' }}>Select at least one branch.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContentScopeFields;
